'use client';

import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from 'react';
import { Fragment } from 'react';
import type { LucideIcon } from 'lucide-react';
import { ArrowRight, ChevronRight, Clock3, Compass, LineChart, Lock, Users } from 'lucide-react';
import { useGetEnabledFeatureFlag } from '@/lib/feature-flags/hooks/use-get-feature-flag';
import { useLabsStore, usePack } from '../pack-context';
import { BrandButton, MetaTag, PillTag } from './_components';
import { StepShell } from './step-shell';

/**
 * Welcome / "Home" screen — the sprint's front door, rendered through the fs-* design system
 * (fs-* tokens + DM Serif Display / Inter).
 *
 * Design notes:
 *  - Two-tone serif headline: the lead clause in `fs-text`, the closing clause in the subtle ink
 *    (`fs-text-subtle`).
 *  - The three steps are an ORDERED SEQUENCE, not three equal plain cards: each is a StepCard
 *    (brand-green numbered badge + a topic icon on the top row + title + supporting line), joined by
 *    chevron CONNECTORS so they read 1 → 2 → 3.
 *  - Meta row: clock + lock pills. Primary CTA carries a trailing arrow.
 *
 * Token mapping (design intent → fs-* token):
 *  - step-badge brand-green #0F4D3A  → `fs-success` (#006e20) — the green token
 *  - step-icon brand-green (55% op)  → `fs-success` at reduced opacity
 *  - card surface #FFFFFF + #E6E4DD  → `fs-surface` + `fs-border`
 *  - connector #C6BFAF               → `fs-icon-subtle`
 */

/**
 * The three cards' ICONS. THREE cards is a structural feature of the sprint (one per module arc:
 * meet the two poles → find your mindset → analyse a real subject), so the icons — and the count —
 * live in the engine and only the WORDS come from the pack. Zipped with `copy.welcome.steps` by
 * index: card i renders `STEP_ICONS[i]` beside `steps[i]`.
 */
const STEP_ICONS: readonly LucideIcon[] = [Users, Compass, LineChart];

/**
 * The v2 promise, and it is deliberately ENGINE-owned rather than pack copy.
 *
 * The archetype reveal sits at `m2-results` for every vertical, so how long the first real result takes
 * is a property of the step sequence, not of the track. Measured on the post-doors cohort with a 2-hour
 * conversion window: welcome to `m2-results` runs about 202 seconds, against 982 seconds for the full
 * flow. Rounding 202s to "about 3 minutes" is honest and it is the number that matters, because the
 * shipped screen leads with 15 minutes — the price — before naming anything the learner gets.
 *
 * The total is still shown, one line down. Promising only the 3 minutes would be a lie of omission and
 * would set up a worse moment later when the sprint keeps going.
 */
const FIRST_RESULT_PROMISE = 'Find out in about 3 minutes.';

/**
 * v2's supporting line, replacing the pack's `welcome.lead`.
 *
 * 🔴 THIS EXISTS BECAUSE THE FIRST DRAFT OF v2 WAS HALF v1. The tail was replaced and the line beneath
 * it was not, so the screen shipped three time signals in three consecutive lines: "about 3 minutes",
 * then "your first financial analysis" (the module 4 artifact, which is the 15-minute payoff), then
 * "About 15 minutes end to end". It also repeated "Your first" twice, two lines apart.
 *
 * Every pack's lead is "Two real career paths." plus its own artifact ("Your first financial analysis"
 * / "product analysis" / "operations analysis"). The first sentence is invariant and the second is
 * exactly the promise v2 is trying to stop leading with, so v2 supplies its own line instead.
 *
 * The artifact is not mentioned above the fold at all now, which is deliberate rather than an
 * oversight. v2's whole premise is that naming a 15-minute deliverable to someone who has not agreed
 * to 15 minutes is what loses them. The total runtime is still stated, one line below the CTA.
 */
const PATHS_PROMISE = 'Two real career paths. See which one fits you.';
export function Welcome() {
  const $ = _c(2);
  const isWelcomeV2 = useGetEnabledFeatureFlag("ff_labs_welcome_v2");
  let t0;
  if ($[0] !== isWelcomeV2) {
    t0 = isWelcomeV2 ? <WelcomeV2 /> : <WelcomeV1 />;
    $[0] = isWelcomeV2;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}

/**
 * v2 — payoff first, and short enough that the CTA is above the fold on a phone.
 *
 * Two changes from v1, both aimed at the same number (42-48% of learners leave this step while every
 * other step holds 92-100%):
 *
 *  1. THE HEADLINE ANSWERS ITSELF. v1 renders `headlineLead` + `headlineTail`, and every pack's tail is
 *     a variant of "Spend 15 minutes finding out" — the cost, in the largest type on the screen, before
 *     the learner has been told what they get. v2 keeps the pack's question and replaces the tail with
 *     {@link FIRST_RESULT_PROMISE}, which still ANSWERS the question rather than sitting beside it: the
 *     pack's line is interrogative, so a tail that is a bare noun phrase leaves the sentence dangling.
 *     Interviews back the framing: learners describe Labs as "the quiz" and remember the
 *     recommendation, not the exercise, so the result is the thing to lead with.
 *     The supporting line is v2's too ({@link PATHS_PROMISE}); leaving the pack's in place was what
 *     made the first draft contradict itself.
 *  2. THE THREE STEP CARDS ARE GONE. They are the tallest block on the screen — v1's own code comment
 *     records them as 391px of an 835px column, pushing Begin below the fold at 393px wide, "the worst
 *     place to hide the primary CTA". They explain a process to someone who has not agreed to the
 *     process yet. Removing them puts the CTA in the first viewport.
 *
 * This is the COLD-ARRIVAL screen. Anyone who came from an offer surface skips it entirely
 * (see `use-welcome-skip.ts`); the people who see this reached the sprint from a direct link or the
 * side-nav rail and genuinely have not been introduced.
 */
export function WelcomeV2() {
  const $ = _c(22);
  const {
    copy
  } = usePack();
  const next = useLabsStore(_temp);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <span className="opacity-70">· Discovery experience</span>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== copy.welcome.eyebrow) {
    t1 = <PillTag>{copy.welcome.eyebrow} {t0}</PillTag>;
    $[1] = copy.welcome.eyebrow;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <span className="text-fs-text-subtle">{FIRST_RESULT_PROMISE}</span>;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== copy.welcome.headlineLead) {
    t3 = <h1 className="font-instrument text-fs-display-sm text-fs-text sm:text-fs-display-md">{copy.welcome.headlineLead} {t2}</h1>;
    $[4] = copy.welcome.headlineLead;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <p className="font-medium text-fs-text-subtle text-fs-title-lg tracking-[-1px]">{PATHS_PROMISE}</p>;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t3) {
    t5 = <div className="flex flex-col items-center gap-2 sm:gap-4">{t3}{t4}</div>;
    $[7] = t3;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== copy.welcome.cta || $[10] !== next) {
    t6 = <BrandButton icon={ArrowRight} onClick={next}>{copy.welcome.cta}</BrandButton>;
    $[9] = copy.welcome.cta;
    $[10] = next;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== copy.welcome.metaDuration) {
    t7 = <MetaTag icon={Clock3}>{copy.welcome.metaDuration} end to end</MetaTag>;
    $[12] = copy.welcome.metaDuration;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <MetaTag icon={Lock}>Your work stays on this device</MetaTag>;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== t7) {
    t9 = <div className="flex flex-col items-center gap-2 sm:flex-row sm:justify-center sm:gap-4">{t7}{t8}</div>;
    $[15] = t7;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t1 || $[18] !== t5 || $[19] !== t6 || $[20] !== t9) {
    t10 = <StepShell showProgress={false}><div className="flex flex-col items-center gap-6 text-center sm:gap-8">{t1}{t5}{t6}{t9}</div></StepShell>;
    $[17] = t1;
    $[18] = t5;
    $[19] = t6;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  return t10;
}

/**
 * v1 — the shipped screen, kept intact so `ff_labs_welcome_v2` is a true A/B and rollback is a flag.
 *
 * Exported, with {@link WelcomeV2}, ONLY so Storybook can render each variant directly: PostHog is not
 * initialised there, so the flag always resolves false and the v2 screen would otherwise be unreviewable
 * without a deploy. Product code should always render {@link Welcome} and let the flag decide.
 */
function _temp(s) {
  return s.next;
}
export function WelcomeV1() {
  const $ = _c(33);
  const {
    copy
  } = usePack();
  const next = useLabsStore(_temp2);
  const steps = copy.welcome.steps;
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <span className="opacity-70">· Discovery experience</span>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== copy.welcome.eyebrow) {
    t1 = <PillTag>{copy.welcome.eyebrow} {t0}</PillTag>;
    $[1] = copy.welcome.eyebrow;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== copy.welcome.headlineTail) {
    t2 = <span className="text-fs-text-subtle">{copy.welcome.headlineTail}</span>;
    $[3] = copy.welcome.headlineTail;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== copy.welcome.headlineLead || $[6] !== t2) {
    t3 = <h1 className="font-instrument text-fs-display-sm text-fs-text sm:text-fs-display-md">{copy.welcome.headlineLead} {t2}</h1>;
    $[5] = copy.welcome.headlineLead;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== copy.welcome.lead) {
    t4 = <p className="font-medium text-fs-text-subtle text-fs-title-lg tracking-[-1px]">{copy.welcome.lead}</p>;
    $[8] = copy.welcome.lead;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t3 || $[11] !== t4) {
    t5 = <div className="flex flex-col items-center gap-2 sm:gap-4">{t3}{t4}</div>;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== steps) {
    let t7;
    if ($[15] !== steps.length) {
      t7 = (step, i) => <Fragment key={step.title}><li className="flex sm:flex-1"><StepCard icon={STEP_ICONS[i] ?? Users} n={i + 1} sub={step.sub} title={step.title} /></li>{i < steps.length - 1 ? <li aria-hidden={true} className="hidden items-center justify-center px-2 text-fs-icon-subtle sm:flex"><ChevronRight className="h-4 w-4" strokeWidth={2} /></li> : null}</Fragment>;
      $[15] = steps.length;
      $[16] = t7;
    } else {
      t7 = $[16];
    }
    t6 = steps.map(t7);
    $[13] = steps;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[17] !== t6) {
    t7 = <ol className="flex w-full flex-col items-stretch gap-2 text-left sm:flex-row sm:items-stretch sm:gap-0">{t6}</ol>;
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== copy.welcome.metaDuration) {
    t8 = <MetaTag icon={Clock3}>{copy.welcome.metaDuration}</MetaTag>;
    $[19] = copy.welcome.metaDuration;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <MetaTag icon={Lock}>Your work stays on this device</MetaTag>;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== t8) {
    t10 = <div className="flex flex-col items-center gap-2 sm:flex-row sm:justify-center sm:gap-4">{t8}{t9}</div>;
    $[22] = t8;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== copy.welcome.cta || $[25] !== next) {
    t11 = <BrandButton icon={ArrowRight} onClick={next}>{copy.welcome.cta}</BrandButton>;
    $[24] = copy.welcome.cta;
    $[25] = next;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== t1 || $[28] !== t10 || $[29] !== t11 || $[30] !== t5 || $[31] !== t7) {
    t12 = <StepShell showProgress={false}><div className="flex flex-col items-center gap-4 text-center sm:gap-8">{t1}{t5}{t7}{t10}{t11}</div></StepShell>;
    $[27] = t1;
    $[28] = t10;
    $[29] = t11;
    $[30] = t5;
    $[31] = t7;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  return t12;
}

/**
 * StepCard — one numbered step in the welcome sequence: white surface, hairline border, rounded; a
 * top row with a brand-green numbered badge (left) + a faint topic icon (right); then a bold title
 * and a muted sub-line.
 *
 * Exported for the public preview's own welcome screen (`app/try/[vertical]`), which shows a
 * DIFFERENT set of steps — it runs two modules, not four — but must look like the same product. An
 * export changes nothing for this file's own use.
 */
function _temp2(s) {
  return s.next;
}
export function StepCard(t0) {
  const $ = _c(15);
  const {
    icon: Icon,
    n,
    sub,
    title
  } = t0;
  let t1;
  if ($[0] !== n) {
    t1 = <span className="flex h-7 w-7 items-center justify-center rounded-fs-pill bg-fs-success font-semibold text-[12.8px] text-fs-on-primary tabular-nums">{n}</span>;
    $[0] = n;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== Icon) {
    t2 = <Icon className="h-4 w-4 text-fs-success opacity-55" strokeWidth={2} />;
    $[2] = Icon;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="flex items-center justify-between">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== title) {
    t4 = <span className="font-semibold text-[15px] text-fs-text leading-[1.25] tracking-[-0.25px]">{title}</span>;
    $[7] = title;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== sub) {
    t5 = <span className="text-fs-body-md text-fs-text-subtle leading-[1.4]">{sub}</span>;
    $[9] = sub;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t3 || $[12] !== t4 || $[13] !== t5) {
    t6 = <div className="flex flex-1 flex-col gap-1.5 rounded-fs-card border border-fs-border bg-fs-surface px-4 pt-3 pb-3 transition-all duration-200 ease-out hover:border-fs-success hover:shadow-md motion-safe:hover:-translate-y-0.5 sm:gap-2 sm:px-5 sm:pt-[18px] sm:pb-5">{t3}{t4}{t5}</div>;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
}